import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Button, Space, Table, message, Divider, Row, Col, 
  InputNumber, Input, Select, Modal, Form, Tag, Alert, Checkbox,
  Tooltip, Progress
} from 'antd';
import { 
  CopyOutlined, DownloadOutlined, WalletOutlined, SendOutlined,
  ReloadOutlined, StopOutlined, DeleteOutlined, EyeOutlined, EyeInvisibleOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// 接口定义
interface ImportedWallet {
  id: string;
  address: string;
  privateKey: string;
  nativeBalance?: string;
  tokenBalance?: string;
  status?: 'idle' | 'pending' | 'success' | 'failed' | 'stopped';
}

interface NetworkConfig {
  name: string;
  rpcUrl: string;
  chainId: number;
  symbol: string;
}

interface FormState {
  toAddress: string;
  contractAddress: string;
  value: string;
  gasLimit: string;
  gasPrice: string;
  delay: number;
  clearBalance: boolean;
  is1559: boolean;
}

// 预设网络配置
const NETWORK_CONFIGS: NetworkConfig[] = [
  {
    name: 'BNB Smart Chain Mainnet',
    rpcUrl: 'https://bsc-dataseed2.bnbchain.org',
    chainId: 56,
    symbol: 'BNB'
  },
  {
    name: 'Ethereum Mainnet',
    rpcUrl: 'https://eth.llamarpc.com',
    chainId: 1,
    symbol: 'ETH'
  },
  {
    name: 'Polygon Mainnet',
    rpcUrl: 'https://polygon-rpc.com',
    chainId: 137,
    symbol: 'MATIC'
  },
  {
    name: 'Arbitrum One',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    chainId: 42161,
    symbol: 'ETH'
  },
  {
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    chainId: 8453,
    symbol: 'ETH'
  },
  {
    name: 'Optimism',
    rpcUrl: 'https://mainnet.optimism.io',
    chainId: 10,
    symbol: 'ETH'
  },
  {
    name: 'Avalanche',
    rpcUrl: 'https://api.avax.network/ext/bc/C/rpc',
    chainId: 43114,
    symbol: 'AVAX'
  },
  {
    name: 'Fantom',
    rpcUrl: 'https://rpc.fantom.network',
    chainId: 250,
    symbol: 'FTM'
  },
];

// ERC20 ABI (精简版)
const ERC20_ABI = [
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address account) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)"
];

const BatchCollectEVM: React.FC = () => {
  // 状态管理
  const [wallets, setWallets] = useState<ImportedWallet[]>([]);
  const [selectedWalletIds, setSelectedWalletIds] = useState<Set<string>>(new Set());
  const [networkConfig, setNetworkConfig] = useState<NetworkConfig>(NETWORK_CONFIGS[0]);
  const [formState, setFormState] = useState<FormState>({
    toAddress: '',
    contractAddress: '',
    value: '',
    gasLimit: '',
    gasPrice: '',
    delay: 1,
    clearBalance: false,
    is1559: true
  });
  const [logs, setLogs] = useState<string[]>([]);
  const [isCollecting, setIsCollecting] = useState(false);
  const [shouldStopCollecting, setShouldStopCollecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [privateKeysInput, setPrivateKeysInput] = useState('');
  const [showPrivateKeys, setShowPrivateKeys] = useState(false);
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [tokenDecimals, setTokenDecimals] = useState(18);
  const [collectType, setCollectType] = useState<'native' | 'token'>('native');

  const logsRef = useRef<HTMLDivElement>(null);

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 工具函数
  const isValidAddress = (address: string): boolean => {
    return ethers.isAddress(address);
  };

  const addLog = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logMessage = `[${timestamp}] ${message}`;
    setLogs(prev => [...prev, logMessage]);
    
    // 自动滚动到底部
    setTimeout(() => {
      if (logsRef.current) {
        logsRef.current.scrollTop = logsRef.current.scrollHeight;
      }
    }, 100);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label}已复制到剪贴板`);
  };

  // 检查Token信息
  const checkTokenInfo = async () => {
    if (!formState.contractAddress || !isValidAddress(formState.contractAddress)) {
      setTokenSymbol('');
      setTokenDecimals(18);
      return;
    }

    try {
      const provider = new ethers.JsonRpcProvider(networkConfig.rpcUrl);
      const tokenContract = new ethers.Contract(formState.contractAddress, ERC20_ABI, provider);

      const [symbol, decimals] = await Promise.all([
        tokenContract.symbol(),
        tokenContract.decimals()
      ]);

      setTokenSymbol(symbol);
      setTokenDecimals(Number(decimals));
      addLog(`Token信息已更新: ${symbol} (${decimals}位小数)`, 'success');
    } catch (error) {
      console.error('获取Token信息失败:', error);
      setTokenSymbol('');
      setTokenDecimals(18);
      addLog('获取Token信息失败，请检查合约地址', 'error');
    }
  };

  // 监听合约地址变化
  useEffect(() => {
    if (formState.contractAddress) {
      checkTokenInfo();
    }
  }, [formState.contractAddress, networkConfig.rpcUrl]);

  // 导入钱包
  const handleImportWallets = () => {
    if (!privateKeysInput.trim()) {
      message.error('请输入私钥');
      return;
    }

    const privateKeys = privateKeysInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const newWallets: ImportedWallet[] = [];
    const invalidKeys: string[] = [];

    privateKeys.forEach((privateKey, index) => {
      try {
        const wallet = new ethers.Wallet(privateKey);
        newWallets.push({
          id: `wallet_${Date.now()}_${index}`,
          address: wallet.address,
          privateKey: privateKey,
          status: 'idle'
        });
      } catch (error) {
        invalidKeys.push(`第${index + 1}行`);
      }
    });

    if (invalidKeys.length > 0) {
      message.error(`发现${invalidKeys.length}个无效私钥: ${invalidKeys.join(', ')}`);
      return;
    }

    if (newWallets.length === 0) {
      message.error('没有有效的私钥');
      return;
    }

    setWallets(prev => [...prev, ...newWallets]);
    setIsImportModalVisible(false);
    setPrivateKeysInput('');
    addLog(`成功导入${newWallets.length}个钱包`, 'success');
    message.success(`成功导入${newWallets.length}个钱包`);

    // 自动刷新余额
    setTimeout(() => {
      refreshBalances();
    }, 500);
  };

  // 刷新余额
  const refreshBalances = async () => {
    if (wallets.length === 0) {
      message.warning('没有钱包需要刷新');
      return;
    }

    setLoading(true);
    addLog('开始刷新余额...', 'info');

    try {
      const provider = new ethers.JsonRpcProvider(networkConfig.rpcUrl);
      const updatedWallets = [...wallets];

      for (let i = 0; i < updatedWallets.length; i++) {
        const wallet = updatedWallets[i];

        try {
          // 获取主币余额
          const nativeBalance = await provider.getBalance(wallet.address);
          wallet.nativeBalance = ethers.formatEther(nativeBalance);

          // 获取Token余额（如果有合约地址）
          if (formState.contractAddress && isValidAddress(formState.contractAddress)) {
            const tokenContract = new ethers.Contract(formState.contractAddress, ERC20_ABI, provider);
            const tokenBalance = await tokenContract.balanceOf(wallet.address);
            wallet.tokenBalance = ethers.formatUnits(tokenBalance, tokenDecimals);
          }

          addLog(`已更新 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 余额`, 'info');
        } catch (error) {
          console.error(`获取 ${wallet.address} 余额失败:`, error);
          addLog(`获取 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 余额失败`, 'error');
        }
      }

      setWallets(updatedWallets);
      addLog('余额刷新完成', 'success');
      message.success('余额刷新完成');
    } catch (error) {
      console.error('刷新余额失败:', error);
      addLog('刷新余额失败', 'error');
      message.error('刷新余额失败');
    } finally {
      setLoading(false);
    }
  };

  // 停止归集
  const stopCollecting = () => {
    setShouldStopCollecting(true);
    addLog('操作已由用户手动停止', 'info');
  };

  // 核心归集功能
  const handleCollect = async (type: 'native' | 'token') => {
    // 前置检查
    if (!isValidAddress(formState.toAddress)) {
      message.error('请输入有效的归集目标地址');
      return;
    }

    if (type === 'token' && (!formState.contractAddress || !isValidAddress(formState.contractAddress))) {
      message.error('归集Token需要输入有效的合约地址');
      return;
    }

    if (selectedWalletIds.size === 0) {
      message.error('请至少选择一个钱包');
      return;
    }

    // 启动归集
    setIsCollecting(true);
    setCollectType(type);
    setShouldStopCollecting(false);
    addLog(`开始${type === 'native' ? networkConfig.symbol : tokenSymbol}归集任务...`, 'info');

    try {
      const provider = new ethers.JsonRpcProvider(networkConfig.rpcUrl);
      const selectedWallets = wallets.filter(w => selectedWalletIds.has(w.id));

      // 更新钱包状态为pending
      const updatedWallets = wallets.map(w =>
        selectedWalletIds.has(w.id) ? { ...w, status: 'pending' as const } : w
      );
      setWallets(updatedWallets);

      for (const wallet of selectedWallets) {
        // 检查停止信号
        if (shouldStopCollecting) {
          addLog('归集任务已停止', 'info');
          break;
        }

        try {
          addLog(`开始为地址 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 进行归集...`, 'info');

          const signer = new ethers.Wallet(wallet.privateKey, provider);
          const feeData = await provider.getFeeData();

          let tx: any = {
            to: formState.toAddress,
            gasLimit: formState.gasLimit || (type === 'native' ? '21000' : '65000'),
          };

          // 设置Gas价格
          if (formState.is1559) {
            tx.maxFeePerGas = feeData.maxFeePerGas;
            tx.maxPriorityFeePerGas = feeData.maxPriorityFeePerGas;
          } else {
            tx.gasPrice = formState.gasPrice ?
              ethers.parseUnits(formState.gasPrice, 'gwei') :
              feeData.gasPrice;
          }

          if (type === 'native') {
            // 归集主币
            if (formState.clearBalance) {
              const balance = await provider.getBalance(signer.address);
              const gasLimit = BigInt(tx.gasLimit);
              const gasPrice = tx.gasPrice || tx.maxFeePerGas || feeData.gasPrice;
              const gasCost = gasLimit * gasPrice;
              const valueToSend = balance - gasCost;

              if (valueToSend <= BigInt(0)) {
                addLog(`${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 余额不足支付Gas费用`, 'error');
                setWallets(prev => prev.map(w =>
                  w.id === wallet.id ? { ...w, status: 'failed' } : w
                ));
                continue;
              }

              tx.value = valueToSend;
            } else {
              if (!formState.value) {
                addLog(`${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 未设置归集数量`, 'error');
                setWallets(prev => prev.map(w =>
                  w.id === wallet.id ? { ...w, status: 'failed' } : w
                ));
                continue;
              }
              tx.value = ethers.parseEther(formState.value);
            }
          } else {
            // 归集Token
            tx.to = formState.contractAddress;
            tx.value = BigInt(0);

            const tokenContract = new ethers.Contract(formState.contractAddress, ERC20_ABI, signer);

            let amount: bigint;
            if (formState.clearBalance) {
              amount = await tokenContract.balanceOf(signer.address);
            } else {
              if (!formState.value) {
                addLog(`${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 未设置归集数量`, 'error');
                setWallets(prev => prev.map(w =>
                  w.id === wallet.id ? { ...w, status: 'failed' } : w
                ));
                continue;
              }
              amount = ethers.parseUnits(formState.value, tokenDecimals);
            }

            if (amount <= BigInt(0)) {
              addLog(`${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} Token余额不足`, 'error');
              setWallets(prev => prev.map(w =>
                w.id === wallet.id ? { ...w, status: 'failed' } : w
              ));
              continue;
            }

            tx.data = tokenContract.interface.encodeFunctionData('transfer', [formState.toAddress, amount]);
          }

          // 发送交易
          const txResponse = await signer.sendTransaction(tx);
          addLog(`归集成功，地址 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}, TxHash: ${txResponse.hash}`, 'success');

          // 等待交易确认
          await txResponse.wait();
          addLog(`交易已确认: ${txResponse.hash}`, 'success');

          // 更新钱包状态为成功
          setWallets(prev => prev.map(w =>
            w.id === wallet.id ? { ...w, status: 'success' } : w
          ));

        } catch (error: any) {
          console.error(`归集失败 ${wallet.address}:`, error);
          addLog(`归集失败 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}: ${error.message || error}`, 'error');

          // 更新钱包状态为失败
          setWallets(prev => prev.map(w =>
            w.id === wallet.id ? { ...w, status: 'failed' } : w
          ));
        }

        // 延迟
        if (formState.delay > 0) {
          await new Promise(resolve => setTimeout(resolve, formState.delay * 1000));
        }
      }

      addLog('所有选定钱包归集任务已完成', 'success');

      // 自动刷新余额
      setTimeout(() => {
        refreshBalances();
      }, 2000);

    } catch (error: any) {
      console.error('归集任务失败:', error);
      addLog(`归集任务失败: ${error.message || error}`, 'error');
      message.error('归集任务失败');
    } finally {
      setIsCollecting(false);
      setShouldStopCollecting(false);

      // 重置未完成的钱包状态
      setWallets(prev => prev.map(w =>
        w.status === 'pending' ? { ...w, status: shouldStopCollecting ? 'stopped' : 'idle' } : w
      ));
    }
  };

  // 导出结果到Excel
  const exportToExcel = () => {
    if (wallets.length === 0) {
      message.warning('没有数据可导出');
      return;
    }

    try {
      const exportData = wallets.map((wallet, index) => ({
        '序号': index + 1,
        '钱包地址': wallet.address,
        '私钥': wallet.privateKey,
        [`${networkConfig.symbol}余额`]: wallet.nativeBalance || '0',
        [`${tokenSymbol || 'Token'}余额`]: wallet.tokenBalance || '0',
        '状态': {
          idle: '待处理',
          pending: '处理中',
          success: '成功',
          failed: '失败',
          stopped: '已停止'
        }[wallet.status || 'idle']
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '归集结果');

      // 设置列宽
      const colWidths = [
        { wch: 8 },  // 序号
        { wch: 45 }, // 钱包地址
        { wch: 70 }, // 私钥
        { wch: 20 }, // 主币余额
        { wch: 20 }, // Token余额
        { wch: 12 }, // 状态
      ];
      worksheet['!cols'] = colWidths;

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const fileName = `EVM批量归集结果_${networkConfig.name}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      saveAs(data, fileName);

      addLog(`导出Excel成功: ${fileName}`, 'success');
      message.success('导出Excel成功');
    } catch (error) {
      console.error('导出Excel失败:', error);
      addLog('导出Excel失败', 'error');
      message.error('导出Excel失败');
    }
  };

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        EVM批量归集工具
      </h1>

      {/* 顶部操作按钮 */}
      <Card className="spacing-responsive">
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshBalances}
            loading={loading}
            style={buttonStyle}
          >
            刷新余额
          </Button>
          <Button
            icon={<WalletOutlined />}
            onClick={() => setIsImportModalVisible(true)}
            style={buttonStyle}
          >
            导入钱包
          </Button>

          {wallets.length > 0 && (
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              style={buttonStyle}
            >
              导出结果
            </Button>
          )}
        </Space>
      </Card>

      {/* 网络与表单配置区域 */}
      <Card className="spacing-responsive">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <div>
              <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '500' }}>
                选择网络
              </h3>
              <Select
                value={networkConfig.name}
                onChange={(value) => {
                  const config = NETWORK_CONFIGS.find(c => c.name === value);
                  if (config) setNetworkConfig(config);
                }}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="选择网络"
              >
                {NETWORK_CONFIGS.map(config => (
                  <Select.Option key={config.name} value={config.name}>
                    {config.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} sm={12}>
            <div>
              <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '500' }}>
                RPC URL
              </h3>
              <Input
                value={networkConfig.rpcUrl}
                onChange={(e) => setNetworkConfig(prev => ({ ...prev, rpcUrl: e.target.value }))}
                style={componentStyle}
                placeholder="RPC URL"
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* 归集配置区域 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          归集配置
        </h3>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                归集到地址 *
              </h4>
              <Input
                value={formState.toAddress}
                onChange={(e) => setFormState(prev => ({ ...prev, toAddress: e.target.value }))}
                style={componentStyle}
                placeholder="输入归集目标地址"
              />
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Token合约地址 (归集Token时必填)
              </h4>
              <Input
                value={formState.contractAddress}
                onChange={(e) => setFormState(prev => ({ ...prev, contractAddress: e.target.value }))}
                style={componentStyle}
                placeholder="输入Token合约地址"
                suffix={tokenSymbol && (
                  <Tag color="blue" style={{ margin: 0 }}>
                    {tokenSymbol}
                  </Tag>
                )}
              />
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                归集数量
              </h4>
              <Input
                value={formState.value}
                onChange={(e) => setFormState(prev => ({ ...prev, value: e.target.value }))}
                style={componentStyle}
                placeholder="归集数量"
                disabled={formState.clearBalance}
              />
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Gas Limit
              </h4>
              <Input
                value={formState.gasLimit}
                onChange={(e) => setFormState(prev => ({ ...prev, gasLimit: e.target.value }))}
                style={componentStyle}
                placeholder="21000"
              />
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Gas Price (Gwei)
              </h4>
              <Input
                value={formState.gasPrice}
                onChange={(e) => setFormState(prev => ({ ...prev, gasPrice: e.target.value }))}
                style={componentStyle}
                placeholder="自动获取"
                disabled={formState.is1559}
              />
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                交易间隔 (秒)
              </h4>
              <InputNumber
                value={formState.delay}
                onChange={(value) => setFormState(prev => ({ ...prev, delay: value || 1 }))}
                min={0.1}
                max={60}
                step={0.1}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="1"
              />
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div style={{ paddingTop: '24px' }}>
              <Space direction="vertical">
                <Checkbox
                  checked={formState.clearBalance}
                  onChange={(e) => setFormState(prev => ({ ...prev, clearBalance: e.target.checked }))}
                >
                  余额清0 (忽略归集数量，转出所有余额)
                </Checkbox>
                <Checkbox
                  checked={formState.is1559}
                  onChange={(e) => setFormState(prev => ({ ...prev, is1559: e.target.checked }))}
                >
                  使用EIP-1559 (推荐)
                </Checkbox>
              </Space>
            </div>
          </Col>
        </Row>

        {/* 执行按钮区域 */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Space size="large">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => handleCollect('native')}
              disabled={selectedWalletIds.size === 0 || isCollecting}
              loading={isCollecting && collectType === 'native'}
              style={primaryButtonStyle}
              size="large"
            >
              归集{networkConfig.symbol}
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => handleCollect('token')}
              disabled={selectedWalletIds.size === 0 || isCollecting || !formState.contractAddress}
              loading={isCollecting && collectType === 'token'}
              style={primaryButtonStyle}
              size="large"
            >
              归集Token
            </Button>
            {isCollecting && (
              <Button
                icon={<StopOutlined />}
                onClick={stopCollecting}
                danger
                style={buttonStyle}
                size="large"
              >
                停止归集
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* 钱包表格区域 */}
      {wallets.length > 0 && (
        <Card className="spacing-responsive">
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
              钱包列表 ({wallets.length}个)
            </h3>
            <Space>
              <Button
                size="small"
                icon={showPrivateKeys ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setShowPrivateKeys(!showPrivateKeys)}
                style={buttonStyle}
              >
                {showPrivateKeys ? '隐藏' : '显示'}私钥
              </Button>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setWallets([]);
                  setSelectedWalletIds(new Set());
                  addLog('已清空所有钱包', 'info');
                }}
                style={buttonStyle}
              >
                清空
              </Button>
            </Space>
          </div>

          <Table
            columns={[
              {
                title: (
                  <Checkbox
                    checked={selectedWalletIds.size === wallets.length && wallets.length > 0}
                    indeterminate={selectedWalletIds.size > 0 && selectedWalletIds.size < wallets.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWalletIds(new Set(wallets.map(w => w.id)));
                      } else {
                        setSelectedWalletIds(new Set());
                      }
                    }}
                  />
                ),
                dataIndex: 'selected',
                width: 50,
                render: (_, record) => (
                  <Checkbox
                    checked={selectedWalletIds.has(record.id)}
                    onChange={(e) => {
                      const newSelected = new Set(selectedWalletIds);
                      if (e.target.checked) {
                        newSelected.add(record.id);
                      } else {
                        newSelected.delete(record.id);
                      }
                      setSelectedWalletIds(newSelected);
                    }}
                  />
                ),
              },
              {
                title: '序',
                dataIndex: 'index',
                width: 60,
                render: (_, __, index) => index + 1,
              },
              {
                title: '地址',
                dataIndex: 'address',
                width: 300,
                render: (text: string) => (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {text.slice(0, 6)}...{text.slice(-4)}
                    </span>
                    <Tooltip title="复制地址">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(text, '地址')}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: '私钥',
                dataIndex: 'privateKey',
                width: 200,
                render: (text: string) => (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {showPrivateKeys ? `${text.slice(0, 10)}...${text.slice(-4)}` : '••••••••••••••••'}
                    </span>
                    <Tooltip title="复制私钥">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(text, '私钥')}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: `${networkConfig.symbol}余额`,
                dataIndex: 'nativeBalance',
                width: 120,
                render: (text: string) => text || '-',
              },
              {
                title: `${tokenSymbol || 'Token'}余额`,
                dataIndex: 'tokenBalance',
                width: 120,
                render: (text: string) => text || '-',
              },
              {
                title: '状态',
                dataIndex: 'status',
                width: 80,
                render: (status: string) => {
                  const statusConfig = {
                    idle: { color: 'default', text: '待处理' },
                    pending: { color: 'processing', text: '处理中' },
                    success: { color: 'success', text: '成功' },
                    failed: { color: 'error', text: '失败' },
                    stopped: { color: 'warning', text: '已停止' }
                  };
                  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.idle;
                  return <Tag color={config.color}>{config.text}</Tag>;
                },
              },
            ]}
            dataSource={wallets}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个钱包`,
            }}
            scroll={{ x: 1000 }}
            size="small"
          />

          {/* 表格底部汇总 */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f5f5f5',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <Row gutter={16}>
              <Col span={8}>
                <strong>总选中: {selectedWalletIds.size} 个</strong>
              </Col>
              <Col span={8}>
                <strong>总{networkConfig.symbol}余额: {
                  wallets
                    .filter(w => selectedWalletIds.has(w.id))
                    .reduce((sum, w) => sum + (parseFloat(w.nativeBalance || '0')), 0)
                    .toFixed(6)
                }</strong>
              </Col>
              <Col span={8}>
                <strong>总{tokenSymbol || 'Token'}余额: {
                  wallets
                    .filter(w => selectedWalletIds.has(w.id))
                    .reduce((sum, w) => sum + (parseFloat(w.tokenBalance || '0')), 0)
                    .toFixed(6)
                }</strong>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {/* 日志区域 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          操作日志
        </h3>
        <div
          ref={logsRef}
          style={{
            height: '200px',
            overflow: 'auto',
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        >
          {logs.length === 0 ? (
            <div style={{ color: '#999', textAlign: 'center', paddingTop: '60px' }}>
              暂无日志
            </div>
          ) : (
            logs.map((log, index) => (
              <div key={index} style={{ marginBottom: '4px' }}>
                {log}
              </div>
            ))
          )}
        </div>
        <div style={{ marginTop: '8px', textAlign: 'right' }}>
          <Button
            size="small"
            onClick={() => setLogs([])}
            style={buttonStyle}
          >
            清空日志
          </Button>
        </div>
      </Card>

      {/* 导入钱包模态框 */}
      <Modal
        title="导入钱包私钥"
        open={isImportModalVisible}
        onOk={handleImportWallets}
        onCancel={() => {
          setIsImportModalVisible(false);
          setPrivateKeysInput('');
        }}
        width={600}
        okText="确认导入"
        cancelText="取消"
      >
        <Alert
          message="安全提醒"
          description="私钥仅在本地浏览器处理，不会上传或存储到服务器。页面关闭后私钥信息将从内存中清除。"
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="vertical">
          <Form.Item
            label="私钥列表 (每行一个私钥)"
            required
          >
            <Input.TextArea
              value={privateKeysInput}
              onChange={(e) => setPrivateKeysInput(e.target.value)}
              placeholder="请粘贴私钥，每行一个&#10;例如：&#10;0x1234567890abcdef...&#10;0xabcdef1234567890..."
              rows={8}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 安全提示 */}
      <Card size="small" style={{
        background: '#fff2f0',
        border: '1px solid #ffccc7',
        marginTop: '20px'
      }}>
        <div style={{ color: '#ff4d4f' }}>
          <strong>⚠️ 安全提醒：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>请妥善保管私钥，任何人获得私钥都可以控制您的钱包</li>
            <li>建议在安全的离线环境中使用此功能</li>
            <li>归集前请确认目标地址正确，交易一旦发送无法撤销</li>
            <li>建议先用小额测试，确认无误后再进行大额归集</li>
            <li>私钥仅在本地浏览器处理，不会上传到服务器</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default BatchCollectEVM;
